import Launch20 from '@carbon/icons-react/es/launch/20';
import OpenPanelFilledLeft20 from '@carbon/icons-react/es/open-panel--filled--left/20';
import { Space, Tooltip, Typography } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { DISPLAY_FORMAT, SpaceTabId } from 'api/models';
import { Project } from 'api/project';
import { SpaceTabDocumentType } from 'api/space/space-tab';
import { CustomerPin } from 'api/spotlight/types';
import { humanise } from 'components/atoms/humanised-date';
import { Loading } from 'components/atoms/loading';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import { FilterBar } from 'components/molecules/filter-bar';
import { Document } from 'components/organisms/document';
import { FreezeAction } from 'components/organisms/document/document.types';
import { SpaceTabMoreActions } from 'components/organisms/projects/project-spaces/sidebar/more-actions';
import { useCurrentSpace } from 'components/organisms/spaces/context';
import { UrlParamTypes } from 'components/pages/project-details/project-spaces/project-spaces.types';
import { useSpaceActions } from 'components/pages/project-details/project-spaces/use-space-actions';
import { useProjectSpacesRearrangeModalContext } from 'contexts/providers/rearrange-modal';
import { setActiveDocument } from 'data/documents';
import { SpaceTab as SpaceTabT, updateSpaceTabTitle } from 'data/space-tabs';
import { getActiveTabKey } from 'data/space-tabs/selectors';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { useIsResponsive } from 'hooks/use-media-query';
import { useTranslation } from 'hooks/use-translation';
import { useSpaceTabTitleMutation } from 'queries/project-spaces';
import { getUserLabel } from 'utils/get-user-label';
import { AiFillMeta } from 'vendor/ckeditor/react-components/ai-fill/types';
import { CurrentSpaceTabContext } from './context';
import { useIframeHeight } from './iframe';
import { PopoverContent } from './popover-content';
import styles from './space-tab.module.css';
import {
  EmbeddedBy,
  FilterBarSecondary,
  Iframely,
  IframeWrapper,
  LoadingWrapper,
  StyledSidebarToggleButton,
  TabIframeWrapper,
} from './styles';

const StyledText = styled(Typography.Text)`
  max-width: 200px;
`;

export const FilterBarStyled = styled(FilterBar)`
  padding: var(--spacing-03) var(--spacing-03);
`;

const TertiaryButtonStyled = styled(TertiaryButton)`
  border: none;
  color: inherit;

  svg {
    width: var(--spacing-05);
    height: var(--spacing-05);
  }
`;

// TODO: @krishna ? is temporary as the other code uses it.
interface SpaceTabProps {
  data: SpaceTabT;
  project: Project;
  isResponsive?: boolean;
}

const getProjectIdAndDocumentId = (url = ''): [number, number] => {
  const arr = url.split('/');

  if (arr.length !== 4) {
    throw new Error(`${url} Rocketlane document url is not correct`);
  }

  return [Number(arr[1]), Number(arr[3])];
};

const DocumentContainer = styled.div`
  width: 100%;
`;

interface RenderRocketlaneDocumentProps {
  data: SpaceTabT;
  readonly?: boolean;
  onFrozenStateChange?: (action: FreezeAction) => void;
  onSpotlightClick?: (pin: CustomerPin) => void;
  aiFillMeta?: AiFillMeta;
}

export const RenderRocketlaneDocument: React.FC<RenderRocketlaneDocumentProps> =
  ({ data, onSpotlightClick, readonly, onFrozenStateChange, aiFillMeta }) => {
    const isResponsive = useIsResponsive();
    const { url, spaceTabId } = data;
    const [projectId, documentId] = getProjectIdAndDocumentId(url);
    const activeKey = useSelector(getActiveTabKey);
    const { private: isPrivate } = useCurrentSpace();

    const { mutate: titleUpdateMutation } = useSpaceTabTitleMutation(
      SpaceTabId(projectId, data.space, documentId),
    );
    const projectResource = useProjectAsRBACResource(projectId);
    const { hasAccessToResource } = useWithAccess();
    const hasEditAccess = hasAccessToResource({
      rule: 'DOCUMENT:WRITE',
      resourceData: projectResource,
    });

    const dispatch = useDispatch();

    useEffect(() => {
      if (parseInt(activeKey, 10) === spaceTabId) {
        dispatch(
          setActiveDocument({
            documentId,
            projectId,
          }),
        );
      }
    }, [dispatch, activeKey, spaceTabId, documentId, projectId]);

    const onDocumentTitleChange = debounce((title) => {
      dispatch(
        updateSpaceTabTitle({
          spaceId: spaceTabId,
          spaceTabTitle: title,
        }),
      );

      titleUpdateMutation(title);
    }, 500);

    const debouncedTitleChange = useCallback(onDocumentTitleChange, [
      onDocumentTitleChange,
    ]);

    // @sha please use this to render the more options
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const renderExtraToolbarOptions = () => {
      return isResponsive ? (
        <></>
      ) : (
        <SpaceTabMoreActions
          spaceTab={data as any}
          projectId={projectId}
          showEdit={false}
        />
      );
    };

    return (
      <DocumentContainer data-test-id="space.tab">
        <Document
          className={styles.document}
          topbarClassName={styles.document_topbar_mod}
          wrapperClassName={styles.document_wrapper}
          editorClassName={styles.document_wrapper}
          projectId={projectId}
          documentId={documentId}
          onDocumentTitleChange={debouncedTitleChange}
          renderExtraToolbarOptions={renderExtraToolbarOptions}
          isPrivate={isPrivate}
          isResponsive={isResponsive}
          readOnly={!hasEditAccess || readonly}
          onFrozenStateChange={onFrozenStateChange}
          onSpotlightClick={onSpotlightClick}
          aiFillMeta={aiFillMeta}
        />
      </DocumentContainer>
    );
  };

export const SpaceTab: FC<SpaceTabProps> = (props) => {
  const { data, project, isResponsive } = props;
  const ref = useRef<any>();
  const height = useIframeHeight(ref, data.metaData?.html || data.url);
  const { spaceTabId, spaceId } = useParams<UrlParamTypes>();
  const { currentSpaceDetails: currentSpace } = useSpaceActions({
    spaceId,
    projectId: Number(project.projectId),
    spaceTabId,
  });

  const sidebarCollapseText = useTranslation('collapse_sidebar');

  const { active, exit, enter } = useDistractionFreeMode();

  function sidebarToggle() {
    if (active) {
      exit();
    } else {
      enter();
    }
  }

  const currentSpaceName = currentSpace?.spaceName || '';
  const currentSpaceTabTitle =
    currentSpace?.spaceTabs.find(
      (spaceTab) => spaceTab.spaceTabId === Number(spaceTabId),
    )?.spaceTabTitle || '';

  const { isRearrangeModalVisible } = useProjectSpacesRearrangeModalContext();

  return data ? (
    <CurrentSpaceTabContext.Provider key={data.spaceTabId} value={data}>
      {data.spaceTabType === SpaceTabDocumentType.RocketlaneDocument ? (
        <RenderRocketlaneDocument
          data={data}
          aiFillMeta={{ company: project.customer }}
        />
      ) : (
        <>
          <div>
            <FilterBarStyled>
              {data.createdBy ? (
                <>
                  <FilterBar.Primary>
                    {isResponsive ? null : (
                      <Tooltip title={sidebarCollapseText} trigger={['hover']}>
                        <StyledSidebarToggleButton
                          type="text"
                          size="small"
                          onClick={sidebarToggle}
                        >
                          <OpenPanelFilledLeft20 color="var(--blue-60)" />
                        </StyledSidebarToggleButton>
                      </Tooltip>
                    )}
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={currentSpaceName}>
                        <StyledText ellipsis={{ tooltip: false }}>
                          {currentSpaceName}
                        </StyledText>
                      </Tooltip>
                      <span>&nbsp;/&nbsp;</span>
                      <Tooltip title={currentSpaceTabTitle}>
                        <StyledText ellipsis={{ tooltip: false }}>
                          {currentSpaceTabTitle}
                        </StyledText>
                      </Tooltip>
                    </div>
                  </FilterBar.Primary>
                  <FilterBar.Central>
                    <Tooltip
                      title={moment(data.createdAt).format(DISPLAY_FORMAT)}
                      placement="right"
                    >
                      <EmbeddedBy>
                        <FormattedMessage
                          id="space_tab.embedded_by"
                          values={{
                            user: getUserLabel(data.createdBy),
                            time: humanise(moment(data.createdAt)),
                          }}
                        />
                      </EmbeddedBy>
                    </Tooltip>
                  </FilterBar.Central>
                </>
              ) : null}
              <FilterBarSecondary>
                <Space size={8} align="center">
                  <Tooltip
                    title={
                      data.metaData?.meta?.site
                        ? `Open in ${data.metaData?.meta?.site}`
                        : 'Open in new tab'
                    }
                    placement="right"
                  >
                    <TertiaryButtonStyled
                      href={data.url}
                      target="_blank"
                      icon={<Launch20 />}
                      size="small"
                      data-cy="embed.url.open.button"
                    />
                  </Tooltip>

                  <SpaceTabMoreActions
                    spaceTab={data as any}
                    projectId={project.projectId}
                  />
                </Space>
              </FilterBarSecondary>
            </FilterBarStyled>
          </div>
          {/*
            When rearrange modal is open we are disabling pointer events on
            iframe so that rearranging can be done withing the modal as otherwise
            the drag and drop actions are being blocked
          */}
          <TabIframeWrapper isPointerEventsEnabled={!isRearrangeModalVisible}>
            {/* Note: We couldn't find why calendly is not working, so we are directly loading it in iframe. */}
            {/**
             * Adding domains here to use our own iframe instead of iframely
             * More info here: https://iframely.com/docs/react
             */}
            {['calendly', 'jotform', 'hubspot'].some((url) =>
              data.metaData?.url?.includes(url),
            ) || !data.metaData?.html ? (
              <IframeWrapper height={height}>
                <iframe title={data.spaceTabTitle} src={data.url} ref={ref} />
              </IframeWrapper>
            ) : (
              <Iframely html={data.metaData.html} height={height} />
            )}
            <PopoverContent />
          </TabIframeWrapper>
        </>
      )}
    </CurrentSpaceTabContext.Provider>
  ) : (
    <LoadingWrapper>
      <Loading size="large" />
    </LoadingWrapper>
  );
};
